import { useLanguage } from '@/i18n/LanguageContext';
import type { TranslateFn } from '@/i18n/LanguageContext';

/**
 * The second line under a product's name in every Ops list: article, size, colour and barcode.
 * `barcodes` arrives already in the server's canonical order (the SKU's own barcode first, then the
 * rest in a fixed order), so the first one is THE barcode; the others are listed (up to three in
 * total) or counted ("+N"). Nothing here picks a barcode on its own.
 */
export type SkuMetaInput = {
  readonly code?: string | null | undefined;
  readonly article?: string | null | undefined;
  readonly size?: string | null | undefined;
  readonly color?: string | null | undefined;
  readonly barcodes?: readonly string[] | null | undefined;
  /** Single canonical barcode for rows that only carry `barcode`. */
  readonly barcode?: string | null | undefined;
};

export function skuMetaParts(t: TranslateFn, s: SkuMetaInput): string[] {
  const parts: string[] = [];
  if (s.article && s.article !== s.code) parts.push(t('sku.article', { value: s.article }));
  if (s.size) parts.push(t('sku.size', { value: s.size }));
  if (s.color) parts.push(t('sku.color', { value: s.color }));
  const codes = s.barcodes && s.barcodes.length > 0 ? s.barcodes : s.barcode ? [s.barcode] : [];
  const first = codes[0];
  if (first !== undefined) {
    if (codes.length === 1) parts.push(t('sku.barcode', { value: first }));
    else if (codes.length <= 3) parts.push(t('sku.barcodes', { value: codes.join(', ') }));
    else parts.push(t('sku.barcodeMore', { value: first, n: String(codes.length - 1) }));
  }
  return parts;
}

export function SkuMeta(props: SkuMetaInput): JSX.Element | null {
  const { t } = useLanguage();
  const parts = skuMetaParts(t, props);
  if (parts.length === 0) return null;
  return (
    <div className="meta sku-meta" data-testid="sku-meta">
      {parts.join(' · ')}
    </div>
  );
}
